import { CheckIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { InputType, itemCountPerPage } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { getLoanCharges, updateLoanCharge } from 'services'
import { Modal, PlainTable } from 'stories/components'
import { fillObject, formatDate, removeComma, REQUIRED_FIELD_ERROR_MESSAGE, thousandSeperator } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { ILoanCharge } from '../LoanCharges'

const defaultInputs = (): Record<string, InputType> => {
  return {
    unpaid: {
      inputType: 'text',
      type: 'thousandSep',
      required: true,
      title: 'Amount to pay for Unpaid',
      prefix: '$',
    },
    accrued: {
      inputType: 'text',
      type: 'thousandSep',
      readOnly: true,
      title: 'Amount to pay for Accrued',
      prefix: '$',
    },
  }
}

export const LoanChargesModal = ({ onClose }: { onClose: Function }) => {
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [data, setData] = useState<ILoanCharge[]>([])
  const [inputs, setInputs] = useState(defaultInputs())

  const tableData = useMemo(
    () =>
      data.map((item, index) => [
        index + 1,
        formatDate(item.date),
        item.description,
        thousandSeperator(item.interestRate),
        formatDate(item.interestFromDate),
        item.deferred ? 'Yes' : 'No',
        thousandSeperator(item.originalBalance),
        thousandSeperator(item.unpaidBalance),
        thousandSeperator(item.accruedInterest),
        thousandSeperator(item.totalDue),
        <div className="flex">
          <span
            className="btn-icon"
            onClick={() => {
              setSelectedIndex(index)
              setInputs(defaultInputs())
            }}
          >
            <CheckIcon className="w-4 h-4" />
          </span>
        </div>,
      ]),
    [data],
  )

  const refetch = () => {
    setLoading(true)
    getLoanCharges({
      orderBy: 'createdAt',
      orderDir: '-1',
      isLoan: true,
      pageNum: 0,
      count: itemCountPerPage,
    })
      .then((data) => setData(data.results))
      .finally(() => setLoading(false))
  }

  useEffect(() => refetch(), [])

  const onChangeInput = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    if (key == 'unpaid') {
      value = removeComma(value)
      const { unpaidBalance } = data[selectedIndex] || {}
      let accruedAmount = 0
      if (unpaidBalance >= value) accruedAmount = 0
      else accruedAmount = value - unpaidBalance

      newInputs['accrued'].value = accruedAmount
    }
    setInputs(newInputs)
  }

  const renderSelectSection = () => {
    const item = data[selectedIndex]
    if (!item) return null

    const { originalBalance, unpaidBalance, accruedInterest } = item
    return (
      <div className="">
        <p className="mb-4">
          Input amount for Charge <b>{selectedIndex + 1}</b> (Original Balance: ${thousandSeperator(originalBalance)},
          Accrued Interest: ${thousandSeperator(accruedInterest)}, Unpaid Balance: ${thousandSeperator(unpaidBalance)})
        </p>
        <div className="max-w-3xl flex items-center gap-4">
          {Object.keys(inputs).map((key) => (
            <RenderInput Key={key} input={inputs[key]} onChange={onChangeInput} />
          ))}
        </div>
      </div>
    )
  }

  const onSubmit = async () => {
    const item = data[selectedIndex]
    if (selectedIndex == -1 || !item) return

    const newData = fillObject(Object.keys(inputs), (key: string) => removeComma(inputs[key].value || '0'))
    if (!newData.unpaid) {
      const newInputs = cloneDeep(inputs)
      newInputs.unpaid.error = REQUIRED_FIELD_ERROR_MESSAGE
      setInputs(newInputs)
      return
    }

    setLoading(true)
    await updateLoanCharge(item.id, {
      unpaidBalance: Math.max(0, removeComma(item.unpaidBalance) - newData.unpaid),
      accruedInterest: removeComma(item.accruedInterest) + newData.accrued,
    })

    newData.id = item.id
    onClose(newData)
  }

  return (
    <Modal
      title="Loan Charges"
      loading={loading}
      isOpen
      onClose={() => onClose(null)}
      disabled={selectedIndex == -1}
      onOk={onSubmit}
    >
      <div>
        <PlainTable
          header={[
            'No',
            'Date',
            'Description',
            'Interest Rate',
            'Interest From Date',
            'Deferred',
            'Original Balance',
            'Unpaid Balance',
            'Accrued Interest',
            'Total Due',
            'Actions',
          ]}
          data={tableData}
        />

        {selectedIndex != -1 && renderSelectSection()}
      </div>
    </Modal>
  )
}
